/**
 * useProcesses Hook
 *
 * Provides data access for the Process Management hierarchy:
 * Process -> Phases -> Events -> Tasks
 *
 * US-PE-002a: Process Management Data Layer
 * US-P2-010: Process Recipe Integration - Process Restructuring
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '@/supabase-client'

/**
 * Process type constants and labels
 */
export const PROCESS_TYPES = {
  LIQUID_CULTURE: 'liquid_culture',
  SPAWN: 'spawn',
  BLOCK: 'block',
  CULTIVATION: 'cultivation'
}

export const PROCESS_TYPE_LABELS = {
  liquid_culture: 'Liquid Culture',
  spawn: 'Spawn Production',
  block: 'Block Production',
  cultivation: 'Block Cultivation'
}

export const PROCESS_TYPE_OPTIONS = [
  { value: 'liquid_culture', label: 'Liquid Culture' },
  { value: 'spawn', label: 'Spawn Production' },
  { value: 'block', label: 'Block Production' },
  { value: 'cultivation', label: 'Block Cultivation' }
]

/**
 * Standard order for process types in UI
 */
export const PROCESS_TYPE_ORDER = ['liquid_culture', 'spawn', 'block', 'cultivation']

/**
 * Hook to fetch all production processes
 * @param {Object} options - Filter options
 * @param {boolean} options.templatesOnly - Only fetch template processes (default: false)
 * @param {boolean} options.activeOnly - Only fetch active processes (default: true)
 * @param {string} options.strainId - Filter by strain ID
 * @param {string} options.processType - Filter by process type (liquid_culture, spawn, block, cultivation)
 * @returns {Object} { processes, loading, error, refetch }
 */
export function useAllProcesses(options = {}) {
  const [processes, setProcesses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const { templatesOnly = false, activeOnly = true, strainId = null, processType = null } = options

  const fetchProcesses = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('production_processes')
        .select(`
          *,
          strains (
            id,
            name,
            code
          ),
          process_phases (
            id,
            name,
            typical_duration_days,
            sequence_order,
            phase_type
          )
        `)
        .order('name', { ascending: true })

      if (templatesOnly) {
        query = query.eq('is_template', true)
      }

      if (activeOnly) {
        query = query.eq('active', true)
      }

      if (strainId) {
        query = query.eq('strain_id', strainId)
      }

      if (processType) {
        query = query.eq('process_type', processType)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        throw fetchError
      }

      // Transform to add phase_count and sort phases by sequence_order
      const processesWithPhases = (data || []).map(p => ({
        ...p,
        phase_count: p.process_phases?.length || 0,
        phases: (p.process_phases || []).sort((a, b) => a.sequence_order - b.sequence_order)
      }))

      setProcesses(processesWithPhases)
    } catch (err) {
      console.error('[useAllProcesses] Error:', err)
      setError(err.message)
      setProcesses([])
    } finally {
      setLoading(false)
    }
  }, [templatesOnly, activeOnly, strainId, processType])

  useEffect(() => {
    fetchProcesses()
  }, [fetchProcesses])

  return {
    processes,
    loading,
    error,
    refetch: fetchProcesses
  }
}

/**
 * Hook to fetch a single process with its full hierarchy
 * (phases, events, and tasks)
 * @param {string} id - The process UUID
 * @returns {Object} { process, phases, loading, error, refetch }
 */
export function useProcess(id) {
  const [process, setProcess] = useState(null)
  const [phases, setPhases] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchProcess = useCallback(async () => {
    if (!id) {
      setProcess(null)
      setPhases([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Fetch the process
      const { data: processData, error: processError } = await supabase
        .from('production_processes')
        .select(`
          *,
          strains (
            id,
            name,
            code,
            typical_lc_days,
            typical_spawn_days,
            typical_incubation_days,
            typical_fruiting_days
          ),
          strain_families (
            id,
            name,
            code
          )
        `)
        .eq('id', id)
        .single()

      if (processError) {
        throw processError
      }

      // Fetch phases with events, tasks, and recipe templates
      const { data: phasesData, error: phasesError } = await supabase
        .from('process_phases')
        .select(`
          *,
          recipe_template:recipe_templates(
            id,
            name,
            description,
            recipe_type
          ),
          phase_events (
            *,
            event_tasks (*)
          )
        `)
        .eq('process_id', id)
        .order('sequence_order', { ascending: true })

      if (phasesError) {
        throw phasesError
      }

      // Sort events and tasks within each phase
      const sortedPhases = (phasesData || []).map(phase => ({
        ...phase,
        phase_events: (phase.phase_events || [])
          .sort((a, b) => a.sequence_order - b.sequence_order)
          .map(event => ({
            ...event,
            event_tasks: (event.event_tasks || [])
              .sort((a, b) => a.sequence_order - b.sequence_order)
          }))
      }))

      setProcess(processData)
      setPhases(sortedPhases)
    } catch (err) {
      console.error('[useProcess] Error:', err)
      setError(err.message)
      setProcess(null)
      setPhases([])
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchProcess()
  }, [fetchProcess])

  return {
    process,
    phases,
    loading,
    error,
    refetch: fetchProcess
  }
}

/**
 * Hook to fetch the process hierarchy view (flattened)
 * @param {Object} options - Filter options
 * @param {string} options.processId - Filter by specific process
 * @param {boolean} options.templatesOnly - Only fetch templates
 * @returns {Object} { hierarchy, loading, error, refetch }
 */
export function useProcessHierarchy(options = {}) {
  const [hierarchy, setHierarchy] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const { processId = null, templatesOnly = false } = options

  const fetchHierarchy = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('process_hierarchy')
        .select('*')

      if (processId) {
        query = query.eq('process_id', processId)
      }

      if (templatesOnly) {
        query = query.eq('is_template', true)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        throw fetchError
      }

      setHierarchy(data || [])
    } catch (err) {
      console.error('[useProcessHierarchy] Error:', err)
      setError(err.message)
      setHierarchy([])
    } finally {
      setLoading(false)
    }
  }, [processId, templatesOnly])

  useEffect(() => {
    fetchHierarchy()
  }, [fetchHierarchy])

  return {
    hierarchy,
    loading,
    error,
    refetch: fetchHierarchy
  }
}

/**
 * Hook providing mutation functions for process management
 * @returns {Object} { createProcess, updateProcess, deleteProcess, cloneForStrain, updating, updateError }
 */
export function useProcessMutations() {
  const [updating, setUpdating] = useState(false)
  const [updateError, setUpdateError] = useState(null)

  /**
   * Create a new process
   */
  const createProcess = async (processData) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { data, error: createErr } = await supabase
        .from('production_processes')
        .insert(processData)
        .select()
        .single()

      if (createErr) {
        throw createErr
      }

      return { data, error: null }
    } catch (err) {
      console.error('[useProcessMutations] Create error:', err)
      setUpdateError(err.message)
      return { data: null, error: err }
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Update an existing process
   */
  const updateProcess = async (id, updates) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { data, error: updateErr } = await supabase
        .from('production_processes')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single()

      if (updateErr) {
        throw updateErr
      }

      return { data, error: null }
    } catch (err) {
      console.error('[useProcessMutations] Update error:', err)
      setUpdateError(err.message)
      return { data: null, error: err }
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Delete a process
   */
  const deleteProcess = async (id) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { error: deleteErr } = await supabase
        .from('production_processes')
        .delete()
        .eq('id', id)

      if (deleteErr) {
        throw deleteErr
      }

      return { error: null }
    } catch (err) {
      console.error('[useProcessMutations] Delete error:', err)
      setUpdateError(err.message)
      return { error: err }
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Clone a template process for a specific strain
   * Uses the clone_process_for_strain database function
   */
  const cloneForStrain = async (templateId, strainId) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { data, error: rpcErr } = await supabase
        .rpc('clone_process_for_strain', {
          p_template_id: templateId,
          p_strain_id: strainId
        })

      if (rpcErr) {
        throw rpcErr
      }

      return { data, error: null }
    } catch (err) {
      console.error('[useProcessMutations] Clone error:', err)
      setUpdateError(err.message)
      return { data: null, error: err }
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Toggle process active status
   */
  const toggleActive = async (id, active) => {
    return updateProcess(id, { active })
  }

  return {
    createProcess,
    updateProcess,
    deleteProcess,
    cloneForStrain,
    toggleActive,
    updating,
    updateError
  }
}

/**
 * Hook providing mutation functions for process phases
 * @returns {Object} { createPhase, updatePhase, deletePhase, reorderPhases, updating, updateError }
 */
export function usePhaseMutations() {
  const [updating, setUpdating] = useState(false)
  const [updateError, setUpdateError] = useState(null)

  const createPhase = async (phaseData) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { data, error: createErr } = await supabase
        .from('process_phases')
        .insert(phaseData)
        .select()
        .single()

      if (createErr) {
        throw createErr
      }

      return { data, error: null }
    } catch (err) {
      console.error('[usePhaseMutations] Create error:', err)
      setUpdateError(err.message)
      return { data: null, error: err }
    } finally {
      setUpdating(false)
    }
  }

  const updatePhase = async (id, updates) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { data, error: updateErr } = await supabase
        .from('process_phases')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single()

      if (updateErr) {
        throw updateErr
      }

      return { data, error: null }
    } catch (err) {
      console.error('[usePhaseMutations] Update error:', err)
      setUpdateError(err.message)
      return { data: null, error: err }
    } finally {
      setUpdating(false)
    }
  }

  const deletePhase = async (id) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { error: deleteErr } = await supabase
        .from('process_phases')
        .delete()
        .eq('id', id)

      if (deleteErr) {
        throw deleteErr
      }

      return { error: null }
    } catch (err) {
      console.error('[usePhaseMutations] Delete error:', err)
      setUpdateError(err.message)
      return { error: err }
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Reorder phases by updating sequence_order
   * @param {Array} orderedPhaseIds - Array of phase IDs in new order
   */
  const reorderPhases = async (orderedPhaseIds) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const updates = orderedPhaseIds.map((id, index) =>
        supabase
          .from('process_phases')
          .update({ sequence_order: index + 1 })
          .eq('id', id)
      )

      await Promise.all(updates)
      return { error: null }
    } catch (err) {
      console.error('[usePhaseMutations] Reorder error:', err)
      setUpdateError(err.message)
      return { error: err }
    } finally {
      setUpdating(false)
    }
  }

  return {
    createPhase,
    updatePhase,
    deletePhase,
    reorderPhases,
    updating,
    updateError
  }
}

/**
 * Hook providing mutation functions for phase events
 * @returns {Object} { createEvent, updateEvent, deleteEvent, updating, updateError }
 */
export function useEventMutations() {
  const [updating, setUpdating] = useState(false)
  const [updateError, setUpdateError] = useState(null)

  const createEvent = async (eventData) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { data, error: createErr } = await supabase
        .from('phase_events')
        .insert(eventData)
        .select()
        .single()

      if (createErr) {
        throw createErr
      }

      return { data, error: null }
    } catch (err) {
      console.error('[useEventMutations] Create error:', err)
      setUpdateError(err.message)
      return { data: null, error: err }
    } finally {
      setUpdating(false)
    }
  }

  const updateEvent = async (id, updates) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { data, error: updateErr } = await supabase
        .from('phase_events')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (updateErr) {
        throw updateErr
      }

      return { data, error: null }
    } catch (err) {
      console.error('[useEventMutations] Update error:', err)
      setUpdateError(err.message)
      return { data: null, error: err }
    } finally {
      setUpdating(false)
    }
  }

  const deleteEvent = async (id) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { error: deleteErr } = await supabase
        .from('phase_events')
        .delete()
        .eq('id', id)

      if (deleteErr) {
        throw deleteErr
      }

      return { error: null }
    } catch (err) {
      console.error('[useEventMutations] Delete error:', err)
      setUpdateError(err.message)
      return { error: err }
    } finally {
      setUpdating(false)
    }
  }

  return {
    createEvent,
    updateEvent,
    deleteEvent,
    updating,
    updateError
  }
}

/**
 * Hook providing mutation functions for event tasks (templates)
 * @returns {Object} { createTask, updateTask, deleteTask, updating, updateError }
 */
export function useTaskMutations() {
  const [updating, setUpdating] = useState(false)
  const [updateError, setUpdateError] = useState(null)

  const createTask = async (taskData) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { data, error: createErr } = await supabase
        .from('event_tasks')
        .insert(taskData)
        .select()
        .single()

      if (createErr) {
        throw createErr
      }

      return { data, error: null }
    } catch (err) {
      console.error('[useTaskMutations] Create error:', err)
      setUpdateError(err.message)
      return { data: null, error: err }
    } finally {
      setUpdating(false)
    }
  }

  const updateTask = async (id, updates) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { data, error: updateErr } = await supabase
        .from('event_tasks')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (updateErr) {
        throw updateErr
      }

      return { data, error: null }
    } catch (err) {
      console.error('[useTaskMutations] Update error:', err)
      setUpdateError(err.message)
      return { data: null, error: err }
    } finally {
      setUpdating(false)
    }
  }

  const deleteTask = async (id) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { error: deleteErr } = await supabase
        .from('event_tasks')
        .delete()
        .eq('id', id)

      if (deleteErr) {
        throw deleteErr
      }

      return { error: null }
    } catch (err) {
      console.error('[useTaskMutations] Delete error:', err)
      setUpdateError(err.message)
      return { error: err }
    } finally {
      setUpdating(false)
    }
  }

  return {
    createTask,
    updateTask,
    deleteTask,
    updating,
    updateError
  }
}

/**
 * Hook to fetch processes grouped by strain and process type
 * Returns data structured for UI display with strain sections
 * @param {Object} options - Filter options
 * @param {boolean} options.activeOnly - Only fetch active processes (default: true)
 * @returns {Object} { grouped, templates, strains, loading, error, refetch }
 */
export function useProcessesGroupedByStrain(options = {}) {
  const [allProcesses, setAllProcesses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const { activeOnly = true } = options

  const fetchProcesses = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('production_processes')
        .select(`
          *,
          strains (
            id,
            name,
            code
          ),
          strain_families (
            id,
            name,
            code
          ),
          process_phases (
            id,
            name,
            typical_duration_days,
            sequence_order,
            phase_type
          )
        `)
        .order('name', { ascending: true })

      if (activeOnly) {
        query = query.eq('active', true)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        throw fetchError
      }

      // Transform to add phase_count and sort phases by sequence_order
      const processesWithPhases = (data || []).map(p => ({
        ...p,
        phase_count: p.process_phases?.length || 0,
        phases: (p.process_phases || []).sort((a, b) => a.sequence_order - b.sequence_order)
      }))

      setAllProcesses(processesWithPhases)
    } catch (err) {
      console.error('[useProcessesGroupedByStrain] Error:', err)
      setError(err.message)
      setAllProcesses([])
    } finally {
      setLoading(false)
    }
  }, [activeOnly])

  useEffect(() => {
    fetchProcesses()
  }, [fetchProcesses])

  // Group processes by family (or strain for legacy), then by process_type
  const { grouped, templates, strains } = useMemo(() => {
    const templateProcesses = allProcesses.filter(p => p.is_template)
    // Include processes that have either strain_family_id or strain_id
    const assignedProcesses = allProcesses.filter(p => !p.is_template && (p.strain_family_id || p.strain_id))

    // Build unique grouping keys — prefer family, fall back to strain
    const uniqueGroups = []
    const seenIds = new Set()
    assignedProcesses.forEach(p => {
      if (p.strain_family_id && p.strain_families && !seenIds.has(p.strain_family_id)) {
        seenIds.add(p.strain_family_id)
        uniqueGroups.push({ id: p.strain_family_id, name: p.strain_families.name, code: p.strain_families.code, isFamily: true })
      } else if (p.strain_id && p.strains && !seenIds.has(p.strain_id)) {
        seenIds.add(p.strain_id)
        uniqueGroups.push({ id: p.strain_id, name: p.strains.name, code: p.strains.code, isFamily: false })
      }
    })

    uniqueGroups.sort((a, b) => a.name.localeCompare(b.name))

    const groupedByKey = {}
    uniqueGroups.forEach(group => {
      const groupProcesses = assignedProcesses.filter(p =>
        group.isFamily ? p.strain_family_id === group.id : p.strain_id === group.id
      )

      const byType = {}
      PROCESS_TYPE_ORDER.forEach(type => {
        const processesOfType = groupProcesses.filter(p => p.process_type === type)
        if (processesOfType.length > 0) {
          byType[type] = processesOfType
        }
      })

      const untyped = groupProcesses.filter(p => !p.process_type)
      if (untyped.length > 0) {
        byType.untyped = untyped
      }

      groupedByKey[group.id] = {
        strain: group, // StrainSection expects { id, name, code }
        byType
      }
    })

    return {
      grouped: groupedByKey,
      templates: templateProcesses,
      strains: uniqueGroups
    }
  }, [allProcesses])

  return {
    grouped,
    templates,
    strains,
    allProcesses,
    loading,
    error,
    refetch: fetchProcesses
  }
}

export default useAllProcesses
